import React, { useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import {
  service,
  factories,
  models,
  IEmbedConfiguration,
  Report,
} from "powerbi-client";
import {
  getPowerBiReportId,
  GetUserDeptAndUnitDataForReport,
} from "../../Helpers/APIEndpoints/Endpoints";
import { useAuthStore } from "../../AuthStore/AuthStore";

// Initialize the Power BI Service
const powerbiService = new service.Service(
  factories.hpmFactory,
  factories.wpmpFactory,
  factories.routerFactory,
);

const ResolutionMetricsReport: React.FC = () => {
  const { instance, accounts } = useMsal();
  const reportRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { commonContextParams } = useAuthStore();

  const loadReport = async () => {
    try {
      // Milestone Logging to track where the execution is
      console.log("ResolutionMetricsReport: Initiating load sequence...");
      setLoading(true);
      setErrorMessage(null);

      const userId = commonContextParams?.userId;
      const roleId = parseInt(commonContextParams?.roleId ?? "0", 10);

      if (!userId) {
        console.warn("ResolutionMetricsReport: userId is missing from store.");
        setLoading(false);
        return;
      }

      // --- 1. Fetch User Context (Departments & Business Units) ---
      console.log(
        "ResolutionMetricsReport: Fetching user context for ID:",
        userId,
      );
      const userContextResponse = await fetch(
        `${GetUserDeptAndUnitDataForReport}?userId=${userId}&roleId=${roleId}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );

      if (!userContextResponse.ok) {
        throw new Error(
          `User Context API failed: ${userContextResponse.status}`,
        );
      }

      const userRes = await userContextResponse.json();
      console.log("ResolutionMetricsReport: User context received:", userRes);

      let deptValues: string[] = [];
      let buValues: string[] = [];

      if (userRes.status === 0 && userRes.data) {
        if (Array.isArray(userRes.data.departments)) {
          deptValues = userRes.data.departments
            .map((d: any) => d.name)
            .filter((name: string) => !!name);
        }
        if (Array.isArray(userRes.data.businessUnits)) {
          buValues = userRes.data.businessUnits
            .map((b: any) => b.name)
            .filter((name: string) => !!name);
        }
      }

      // --- 2. Fetch Report Config ---
      console.log(
        "ResolutionMetricsReport: Fetching Report ID/Workspace ID...",
      );
      const reportConfigResponse = await fetch(
        `${getPowerBiReportId}?ReportName=ResolutionMetricsReport`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );

      if (!reportConfigResponse.ok)
        throw new Error("Failed to fetch Report Configuration.");
      const reportResult = await reportConfigResponse.json();

      if (
        reportResult.status === 0 &&
        Array.isArray(reportResult.data) &&
        reportResult.data.length > 0
      ) {
        const reportEntry = reportResult.data[0];
        const configDetails = JSON.parse(reportEntry.ConfigValue);
        const { reportId, workspaceId } = configDetails;

        // --- 3. Token & Embed URL ---
        const tokenRequest = {
          account: accounts[0],
          scopes: ["https://analysis.windows.net/powerbi/api/Report.Read.All"],
        };

        const tokenResponse = await instance
          .acquireTokenSilent(tokenRequest)
          .catch(() => instance.acquireTokenPopup(tokenRequest));

        const accessToken = tokenResponse.accessToken;

        const pbiResponse = await fetch(
          `https://api.powerbi.com/v1.0/myorg/groups/${workspaceId}/reports/${reportId}`,
          { headers: { Authorization: `Bearer ${accessToken}` } },
        );

        if (!pbiResponse.ok)
          throw new Error("Power BI API could not provide Embed URL.");
        const pbiData = await pbiResponse.json();
        console.log("ResolutionMetricsReport: Embed data retrieved.");

        // --- 4. Build Conditional Filters ---
        const reportFilters: any[] = [];
        if (deptValues.length > 0) {
          reportFilters.push({
            $schema: "http://powerbi.com/product/schema#basic",
            target: { table: "Department", column: "Name" },
            operator: "In",
            values: deptValues,
            filterType: models.FilterType.BasicFilter,
          });
        }
        if (buValues.length > 0) {
          reportFilters.push({
            $schema: "http://powerbi.com/product/schema#basic",
            target: { table: "BusinessUnit", column: "Name" },
            operator: "In",
            values: buValues,
            filterType: models.FilterType.BasicFilter,
          });
        }

        // --- 5. Embed ---
        if (reportRef.current) {
          powerbiService.reset(reportRef.current);

          const embedConfig: IEmbedConfiguration = {
            type: "report",
            id: pbiData.id,
            embedUrl: pbiData.embedUrl,
            accessToken: accessToken,
            tokenType: models.TokenType.Aad,
            settings: {
              panes: {
                filters: { visible: true, expanded: false },
                pageNavigation: { visible: true },
              },
              background: models.BackgroundType.Transparent,
            },
          };

          const report = powerbiService.embed(
            reportRef.current,
            embedConfig,
          ) as Report;

          report.off("loaded");
          report.on("loaded", async () => {
            try {
              // Apply filters (or empty array for Super Admin)
              await report.setFilters(reportFilters);
              console.log("ResolutionMetricsReport: Success. Filters applied.");
            } catch (fErr) {
              console.error("ResolutionMetricsReport: Filter Error", fErr);
            }
          });
        }
      } else {
        throw new Error(
          "Report configuration not found for 'ResolutionMetricsReport'.",
        );
      }
    } catch (err: any) {
      console.error("ResolutionMetricsReport: Fatal Error", err);
      setErrorMessage(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Explicitly check for userId property to avoid race conditions
    if (accounts.length > 0 && commonContextParams?.userId) {
      loadReport();
    }
  }, [accounts, instance, commonContextParams?.userId]); // Watch specific property

  return (
    <div style={{ height: "100vh", width: "100%", position: "relative" }}>
      {loading && (
        <div style={{ padding: "20px", textAlign: "center" }}>
          <p>Loading Resolution Metrics Data...</p>
        </div>
      )}

      {errorMessage && (
        <div style={{ color: "red", padding: "20px", textAlign: "center" }}>
          <p>Error: {errorMessage}</p>
          <button onClick={() => loadReport()}>Retry</button>
        </div>
      )}

      <div
        ref={reportRef}
        style={{
          height: "100%",
          width: "100%",
          display: loading || errorMessage ? "none" : "block",
        }}
      />
    </div>
  );
};

export default ResolutionMetricsReport;
